import { Component, Element, h, Prop, Watch, State, Host } from '@stencil/core';
import { ActionTrackingEvent, VirtualPageTrackingEvent } from '@union-investment/tracking/src/events';
import debounce from 'debounce';

export interface UiCmsQuickfactBody {
  iconPath?: string;
  headline?: string;
  text: string;
}

interface SdQuickfactElement extends HTMLElement {
  open: boolean;
  show(): void;
  hide(): void;
  expandable: boolean;
}

@Component({
  tag: 'ui-cms-quickfact',
  styleUrl: 'ui-cms-quickfact.scss',
})
export class UiCmsQuickfact {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) quickfact: SdQuickfactElement;

  @Prop({ mutable: true }) body: UiCmsQuickfactBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) iconSize: 'small' | 'large';

  @Prop({ mutable: true }) iconLayout: 'above' | 'left' = 'above';

  @Prop() inverted: boolean;

  @State() expandable: boolean = false;

  @State() mobileView: boolean;

  private resizeObserver: ResizeObserver;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.updateExpandable();
    this.addResizeObserver();
  }

  componentDidUpdate() {
    this.updateSlotChildrenInverted();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  private updateSlotChildrenInverted() {
    if (!this.inverted) {
      return;
    }
    const slotElement = this.el.querySelector('.slot');
    if (slotElement) {
      Array.from(slotElement.children).forEach((element) => {
        element.setAttribute('inverted', 'true');
      });
    }
  }

  private updateExpandable() {
    const slotDiv = this.el.querySelector('div.slot');
    this.expandable = slotDiv && slotDiv.children.length > 0;
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.mobileView = window.innerWidth < 768;
      }, 200)
    );
    this.resizeObserver.observe(this.el);
  }

  private handleClick() {
    if (!this.quickfact) {
      return;
    }
    this.track();
  }

  private track() {
    const actionTrackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Open Quickfact',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: this.body.text,
        target: window.location.href,
      },
      category: {
        area: 'Content',
      },
    };

    const virtualPageTrackingEvent: VirtualPageTrackingEvent = {
      eventInfo: {
        eventName: 'Change Tab',
        eventType: 'VirtualPage',
      },
      attributes: {
        virtualContentGroups: [this.body.text],
      },
    };

    if (this.quickfact.open) {
      if (actionTrackingEvent) window.ui?.services?.tracking?.track(actionTrackingEvent);
      if (virtualPageTrackingEvent) window.ui?.services?.tracking?.track(virtualPageTrackingEvent);
    }
  }

  private renderMobileView() {
    return (
      <Host class={{ inverted: this.inverted }}>
        <sd-quickfact
          ref={(element) => {
            this.quickfact = element;
          }}
          expandable={this.expandable}
          onClick={() => this.handleClick()}
        >
          {this.renderSDIcon()}
          <div slot="summary">
            <p class="headline">{this.body.headline}</p>
            <div class="subline">{this.body.text}</div>
          </div>
          <div class="slot">
            <slot></slot>
          </div>
        </sd-quickfact>
      </Host>
    );
  }

  private renderSDIcon() {
    return (
      <sd-icon
        color={this.inverted ? 'white' : 'primary'}
        library={window.ui.cmsState.iconLibrary}
        name={this.body.iconPath}
        slot="icon"
      ></sd-icon>
    );
  }

  private renderDesktopView() {
    return (
      <Host
        class={{
          'layout-left': this.iconLayout === 'left',
          'sd-icon--small': this.iconSize === 'small',
          'sd-icon--large': this.iconSize === 'large',
          inverted: this.inverted,
          header: true,
        }}
      >
        <sd-quickfact
          ref={(element) => {
            this.quickfact = element;
          }}
          expandable={this.expandable}
          onClick={() => this.handleClick()}
        >
          {this.iconLayout === 'above' && this.renderSDIcon()}
          <div class={{ 'quickfact-layout--left': this.iconLayout === 'left' }} slot="summary">
            {this.iconLayout === 'left' && this.renderSDIcon()}
            <div class={{ 'quickfact-layout--summary': this.iconLayout === 'left' }}>
              <p class="headline">{this.body.headline}</p>
              <div class="subline">{this.body.text}</div>
            </div>
          </div>
          <div class="slot">
            <slot></slot>
          </div>
        </sd-quickfact>
      </Host>
    );
  }

  render() {
    return this.mobileView ? this.renderMobileView() : this.renderDesktopView();
  }
}
